import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Drawer } from "@mantine/core";
import { resetForm } from "../../../features/formData/formSlice";
import AdminHeader from "../../common/Header";
import { isAdminUser } from "../../navigation/adminNavigation";
import {
  UserCircle,
  PenLine,
  Building2,
  Megaphone,
  Menu,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────────────
   Styles — BEM prefix: tm__
───────────────────────────────────────────────────────────────────────────── */
const css = `
  /* ── Mobile toggle ─────────────────────────────────────────────────────── */
  .tm__toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: "Outfit", sans-serif;
    font-size: 13px;
    font-weight: 600;
    padding: 9px 16px;
    border-radius: 10px;
    border: 1.5px solid #e8e8e8;
    background: #ffffff;
    color: #444444;
    cursor: pointer;
    transition: all 0.25s ease;
  }
  .tm__toggle:hover {
    border-color: #1971c2;
    color: #1971c2;
    background: rgba(25,113,194,0.05);
  }

  /* ── Sidebar card ──────────────────────────────────────────────────────── */
  .tm__sidebar {
    background: #ffffff;
    border: 1px solid #ebebeb;
    border-radius: 20px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  /* ── Nav item ──────────────────────────────────────────────────────────── */
  .tm__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
    padding: 11px 14px;
    border-radius: 12px;
    border: none;
    background: transparent;
    color: #444444;
    font-family: "Outfit", sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.25s ease, color 0.25s ease;
    text-align: left;
  }
  .tm__item:hover {
    background: #f7f7f7;
    color: #1971c2;
  }
  .tm__item--active {
    background: rgba(25,113,194,0.08);
    color: #1971c2;
    font-weight: 600;
  }
  .tm__item--active:hover {
    background: rgba(25,113,194,0.12);
    color: #1971c2;
  }

  /* ── Item left (icon + label) ──────────────────────────────────────────── */
  .tm__item-left {
    display: flex;
    align-items: center;
    gap: 11px;
  }

  /* ── Icon wrapper ──────────────────────────────────────────────────────── */
  .tm__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #f7f7f7;
    color: #888888;
    flex-shrink: 0;
    transition: background 0.25s ease, color 0.25s ease;
  }
  .tm__item--active .tm__icon,
  .tm__item:hover .tm__icon {
    background: rgba(25,113,194,0.1);
    color: #1971c2;
  }

  /* ── Chevron ───────────────────────────────────────────────────────────── */
  .tm__chevron {
    color: #888888;
    flex-shrink: 0;
    transition: transform 0.25s ease, color 0.25s ease;
  }
  .tm__chevron--open {
    transform: rotate(180deg);
    color: #1971c2;
  }

  /* ── Submenu ───────────────────────────────────────────────────────────── */
  .tm__submenu {
    overflow: hidden;
    max-height: 0;
    transition: max-height 0.3s ease;
    padding-left: 16px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .tm__submenu--open {
    max-height: 400px;
  }

  .tm__subitem {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 9px 14px;
    border-radius: 10px;
    border: none;
    background: transparent;
    color: #888888;
    font-family: "Outfit", sans-serif;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.25s ease, color 0.25s ease;
    text-align: left;
  }
  .tm__subitem:hover {
    background: #f7f7f7;
    color: #1971c2;
  }
  .tm__subitem--active {
    color: #1971c2;
    font-weight: 600;
    background: rgba(25,113,194,0.06);
  }

  /* ── Divider ───────────────────────────────────────────────────────────── */
  .tm__divider {
    height: 1px;
    background: #f0f0f0;
    margin: 6px 0;
    border: none;
  }

  /* ── Drawer inner (used in Mantine Drawer body) ────────────────────────── */
  .tm__drawer-inner {
    padding: 8px 0;
  }

  /* ── Active indicator bar ──────────────────────────────────────────────── */
  .tm__item--active::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 60%;
    border-radius: 0 3px 3px 0;
    background: #1971c2;
  }
  .tm__item {
    position: relative;
  }
`;

/* ─────────────────────────────────────────────────────────────────────────────
   Tab config
───────────────────────────────────────────────────────────────────────────── */
const TAB_ITEMS = [
  {
    label: "My Space",
    path: "/dashboard/myspace",
    icon: <UserCircle size={16} />,
  },
  {
    label: "Post",
    path: "/dashboard/postDashboard",
    icon: <PenLine size={16} />,
  },
  {
    label: "Property",
    path: "/dashboard/propertyDashboard",
    icon: <Building2 size={16} />,
    onClick: "resetForm",
  },
  {
    label: "Promotion",
    path: "/dashboard/promotionDashboard",
    icon: <Megaphone size={16} />,
  },
];

/* ─────────────────────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────────────────────── */
const TabMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const authState = useSelector((state) => state.auth);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showDrawer, setShowDrawer] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const adminUser = isAdminUser(authState);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleTabClick = (item, idx) => {
    if (item.subMenu) {
      setOpenSubMenu(openSubMenu === idx ? null : idx);
      return;
    }
    if (item.onClick === "resetForm") dispatch(resetForm());
    navigate(item.path);
    if (isMobile) setShowDrawer(false);
  };

  const handleSubMenuClick = (sub) => {
    navigate(sub.path);
    if (isMobile) setShowDrawer(false);
  };

  if (adminUser) {
    return <AdminHeader />;
  }

  const renderNav = () => (
    <nav className="tm__sidebar">
      {TAB_ITEMS.map((item, idx) => {
        const isSubMenuActive = item.subMenu?.some(
          (sub) => location.pathname === sub.path
        );
        const isActive = item.path
          ? location.pathname === item.path
          : isSubMenuActive;
        const isSubMenuOpen = item.subMenu
          ? openSubMenu === idx || isSubMenuActive
          : false;

        return (
          <div key={item.label}>
            {/* Main item */}
            <button
              type="button"
              className={`tm__item${isActive ? " tm__item--active" : ""}`}
              onClick={() => handleTabClick(item, idx)}
            >
              <span className="tm__item-left">
                <span className="tm__icon">{item.icon}</span>
                <span>{item.label}</span>
              </span>

              {item.subMenu && (
                <ChevronDown
                  size={14}
                  className={`tm__chevron${isSubMenuOpen ? " tm__chevron--open" : ""}`}
                />
              )}
            </button>

            {/* Submenu */}
            {item.subMenu && (
              <div className={`tm__submenu${isSubMenuOpen ? " tm__submenu--open" : ""}`}>
                {item.subMenu.map((sub) => (
                  <button
                    key={sub.label}
                    type="button"
                    className={`tm__subitem${
                      location.pathname === sub.path ? " tm__subitem--active" : ""
                    }`}
                    onClick={() => handleSubMenuClick(sub)}
                  >
                    <span>{sub.icon}</span>
                    <span>{sub.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  return (
    <>
      <style>{css}</style>

      {/* Mobile toggle */}
      {isMobile && (
        <button
          type="button"
          className="tm__toggle"
          onClick={() => setShowDrawer(true)}
          aria-label="Open menu"
        >
          <Menu size={16} />
          Menu
        </button>
      )}

      {/* Desktop sidebar */}
      {!isMobile && renderNav()}

      {/* Mobile — Mantine Drawer (as per design system) */}
      {isMobile && (
        <Drawer
          opened={showDrawer}
          onClose={() => setShowDrawer(false)}
          title="Menu"
          size="xs"
          styles={{
            title: {
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 600,
              fontSize: "16px",
              color: "#080808",
            },
            header: {
              borderBottom: "1px solid #ebebeb",
              paddingBottom: "12px",
            },
            body: { padding: "12px 8px" },
          }}
        >
          {renderNav()}
        </Drawer>
      )}
    </>
  );
};

export default TabMenu;
